<script>
import HomeIcon from "../Icons/HomeIcon.vue";
import LocationIcon from "../Icons/LocationIcon.vue";
import CaregiverIcon from "../Icons/CaregiverIcon.vue";
import HeartIcon from "../Icons/HeartIcon.vue";
import CareIcon from "../Icons/CareIcon.vue";
import WalkerIcon from "../Icons/WalkerIcon.vue";
import PeopleIcon from "../Icons/PeopleIcon.vue";
import ChevronRightIcon from "../Icons/ChevronRight.vue";
import ChatIcon from "../Icons/ChatIcon.vue";
import PostsCarousel from "./PostsCarousel.vue";
import StartseiteNavItem from "./StartseiteNavItem.vue";
import * as routeHelpers from "../../../services/route-helpers";

export default {
  props: {
    isInSecondLevel: {
      type: Boolean,
      required: true,
    },
    secondLevelPortal: {
      type: [Object, null],
      required: false,
    },
    overviewPagesData: {
      type: [Array, null],
      required: false,
    },
  },
  emits: ["goToSecondLevel"],
  components: {
    HomeIcon,
    LocationIcon,
    HeartIcon,
    CareIcon,
    WalkerIcon,
    PeopleIcon,
    ChevronRightIcon,
    PostsCarousel,
    CaregiverIcon,
    StartseiteNavItem,
    ChatIcon,
  },
  data() {
    return {
      currentPath: window.location.pathname,
    };
  },
  computed: {
    iconsPerPortal() {
      return {
        gesundheit: HeartIcon,
        pflege: CareIcon,
        hilfsmittel: WalkerIcon,
        default: HeartIcon, // TODO: Find a default icon
      };
    },
    portalData() {
      if (!this.secondLevelPortal) return null;
      return this.overviewPagesData?.find(
        (page) => page.overviewStory.slug === this.secondLevelPortal.slug
      );
    },
    showInHeaderPosts() {
      return this.portalData?.showInHeaderPosts || [];
    },
    highlightedPosts() {
      return this.portalData?.highlightedPosts || [];
    },
    currentFolderSlug() {
      const page = this.overviewPagesData?.find((page) =>
        this.currentPath.startsWith(`/${page.overviewStory.full_slug}`)
      );

      return page?.parentFolder?.slug;
    },
    isPatientOrInfoPage() {
      return this.isPatientRoute() || this.isInfoPage(this.overviewPagesData);
    },
    ctaData() {
      return this.getCtaData();
    },
    startPageHref() {
      return this.getStartPageUrl();
    },
    aboutUsHref() {
      return this.buildUrlWithSource("ueber-anni");
    },
    showInformationPortal() {
      return (
        this.isPatientOrInfoPage &&
        this.overviewPagesData &&
        this.overviewPagesData?.length > 0
      );
    },
    transitionName() {
      return this.isInSecondLevel ? "slide-left" : "slide-right";
    },
  },
  methods: {
    ...routeHelpers,
  },
};
</script>

<template>
  <div
    class="mobile-header-expanded-content"
    id="mobile-header-expansible-content"
    role="region"
  >
    <transition :name="transitionName" mode="out-in">
      <!-- level 1 -->
      <nav v-if="!isInSecondLevel" key="level1" class="nav">
        <div v-if="ctaData" class="cta-container">
          <a :href="ctaData.url" :target="ctaData.target" class="cta-link">
            Einloggen
          </a>
        </div>

        <div v-if="showInformationPortal" class="nav-section">
          <StartseiteNavItem :href="startPageHref" as="div" />
        </div>

        <div
          v-if="showInformationPortal"
          class="nav-section"
          :class="{ active: isInfoPage(overviewPagesData) }"
        >
          <h2>Informationsportal</h2>

          <ul class="nav-items-list">
            <li
              v-for="page in overviewPagesData"
              :key="page.id"
              class="nav-item"
              :class="{
                active: page.parentFolder.slug === currentFolderSlug,
              }"
            >
              <button
                @click="
                  $emit('goToSecondLevel', {
                    slug: page.overviewStory.slug,
                    name: page.parentFolder.name,
                  })
                "
              >
                <i>
                  <component
                    :is="
                      iconsPerPortal[page.overviewStory.slug] ||
                      iconsPerPortal.default
                    "
                    :color="
                      page.parentFolder.slug === currentFolderSlug
                        ? '#35887e'
                        : undefined
                    "
                  />
                </i>
                <span
                  :class="{
                    active: page.parentFolder.slug === currentFolderSlug,
                  }"
                >
                  {{ page.parentFolder.name }}
                </span>
                <i aria-label="Rechtspfeil-Symbol">
                  <ChevronRightIcon />
                </i>
              </button>
            </li>
          </ul>
        </div>

        <div class="nav-section">
          <ul class="nav-items-list">
            <StartseiteNavItem
              v-if="!showInformationPortal"
              :href="startPageHref"
            />

            <li v-if="isRxpRoute() || isPatientRoute()" class="nav-item">
              <a href="/">
                <i aria-label="Symbol des Standorts">
                  <HeartIcon />
                </i>
                <span>Für Sanitätshäuser</span>
              </a>
            </li>

            <li
              v-if="isB2BRoute()"
              class="nav-item"
              :class="{ active: currentPageIs('/module/mdr') }"
            >
              <a href="/module/mdr">
                <i aria-label="Rollstuhl-Symbol">
                  <WalkerIcon />
                </i>
                <span>EasyCompliance</span>
              </a>
            </li>

            <li
              v-if="isB2BRoute()"
              class="nav-item"
              :class="{ active: currentPageIs('/module/status') }"
            >
              <a href="/module/status">
                <i aria-label="Symbol einer Hand mit einem Herz darüber">
                  <CareIcon />
                </i>
                <span>StatusSync</span>
              </a>
            </li>

            <li
              v-if="isB2BRoute()"
              class="nav-item"
              :class="{ active: currentPageIs('/module/chatbot') }"
            >
              <a href="/module/chatbot">
                <i aria-label="Chat-Symbol">
                  <ChatIcon />
                </i>
                <span>Online-Rezeption</span>
              </a>
            </li>

            <li
              class="nav-item"
              :class="{ active: currentPageIs('/ueber-anni') }"
            >
              <a :href="aboutUsHref">
                <i aria-label="Personen-Symbol">
                  <PeopleIcon />
                </i>
                <span> Über uns </span>
              </a>
            </li>
          </ul>
        </div>
      </nav>

      <!-- level 2 -->
      <nav v-else key="level2" class="nav">
        <!-- highlighted posts -->
        <div class="nav-section" v-if="highlightedPosts.length > 0">
          <PostsCarousel :posts="highlightedPosts" />
        </div>

        <!-- posts list -->
        <div class="nav-section" v-if="showInHeaderPosts.length > 0">
          <h2>Häufig gelesen</h2>
          <ul class="nav-items-list">
            <li
              v-for="post in showInHeaderPosts"
              :key="post.id"
              class="nav-item"
              :class="{ active: `/${post.full_slug}` === currentPath }"
            >
              <a :href="`/${post.full_slug}`">
                <span> {{ post.name }} </span>
                <i aria-label="Rechtspfeil-Symbol">
                  <ChevronRightIcon />
                </i>
              </a>
            </li>
          </ul>
        </div>

        <div class="cta-container">
          <a
            :href="`/${portalData.overviewStory.full_slug}`"
            class="see-more-posts"
          >
            Alle Beiträge anzeigen
          </a>
        </div>
      </nav>
    </transition>
  </div>
</template>

<style lang="scss">
.mobile-header-expanded-content {
  position: fixed;
  top: 60px; // height of the navbar
  left: 0;
  right: 0;
  bottom: 0;
  overflow-y: auto;
  z-index: 1000;
  background-color: #f4f4f4;
  min-height: 100vh;
  height: 100%;
  width: 100%;

  .nav {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    padding-top: 37px;
    margin-bottom: 140px; // ensure we cover the mobile header
    gap: 30px;
  }

  .cta-container {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    padding-left: 16px;
    padding-right: 16px;

    .cta-link {
      background-color: #35887e;
      font-weight: 600;
      font-size: 14px;
      line-height: 134%;
      text-align: center;
      color: #ffffff;
      text-decoration: none;
      padding: 12px 13.1249px;
      border-radius: 5px;
      width: 100%;
      max-width: 261px;
      border: none;
      cursor: pointer;
      transition: background-color 0.1s ease-in-out;

      @media (hover: hover) {
        &:hover {
          background-color: #23615a;
        }
      }
    }

    .see-more-posts {
      display: flex;
      flex-direction: row;
      justify-content: center;
      align-items: center;
      padding: 14px 30px;
      gap: 15px;
      max-width: 261px;
      width: 100%;
      background-color: #35887e;
      border: 2px solid #35887e;
      border-radius: 6px;

      font-weight: 500;
      font-size: 15px;
      line-height: 16px;
      text-align: center;
      color: #ffffff;
      text-decoration: none;

      &:hover {
        background-color: #23615a;
        border: 2px solid #23615a;
      }
    }
  }

  .nav-section {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 7px;
    padding-bottom: 5px;

    h2 {
      width: 100%;
      padding-left: 20px;
      font-weight: 600;
      font-size: 14px;
      line-height: 21px;
      max-width: unset;
    }

    .nav-items-list {
      width: 100%;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 10px;
      padding: 0;
    }

    .nav-item {
      width: 100%;
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 10px;
      background-color: #fff;
      font-weight: 400;
      font-size: 14px;
      line-height: 21px;

      a {
        text-decoration: none;
      }

      button {
        border: none;
        background-color: unset;
        cursor: pointer;
      }

      a,
      button {
        color: #000000;
        width: 100%;
        height: 100%;
        padding: 16px 20px;
        display: flex;
        flex-direction: row;
        justify-content: flex-start;
        gap: 12px;

        i {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 20px;
          width: 20px;

          svg {
            width: 100%;
            height: auto;
          }
        }

        i:last-child {
          margin-left: auto;
        }
      }

      &.active {
        a,
        button {
          color: #35887e;
          font-weight: 600;
        }
      }
    }
  }

  /* Transition styles */
  .slide-left-enter-active,
  .slide-left-leave-active,
  .slide-right-enter-active,
  .slide-right-leave-active {
    transition: all 0.2s ease;
  }

  .slide-left-enter {
    transform: translateX(100%);
  }

  .slide-left-leave-to {
    transform: translateX(-100%);
  }

  .slide-right-enter {
    transform: translateX(-100%);
  }

  .slide-right-leave-to {
    transform: translateX(100%);
  }
}
</style>
